/**
 * useAudioPlayback — React hook wrapping AudioPlaybackQueue.
 *
 * Manages the lifecycle of an AudioPlaybackQueue: creates it on mount,
 * resets it between turns, and closes the AudioContext on unmount.
 *
 * iOS Safari gesture requirement (Pitfall C):
 *   The AudioContext CANNOT start without a prior user gesture on iOS Safari.
 *   The consumer (02-04 UI) MUST call resumeAudioContext() from inside an
 *   onClick or onTouchStart handler BEFORE the first voice turn begins.
 *   Without this, the AudioContext remains suspended and no audio will play.
 *   Document this in the 02-04 UI — it is not a code defect, it is an OS policy.
 *
 * Hook contract for 02-04:
 *   const { enqueueChunk, onAudioDone, isPlaying, resumeAudioContext, reset } = useAudioPlayback();
 *
 *   // In the onClick/onTouchStart that starts voice mode:
 *   await resumeAudioContext();
 *
 *   // For each WebSocket 'audio_chunk' frame:
 *   enqueueChunk(frame.data);
 *
 *   // For the WebSocket 'audio_done' frame:
 *   onAudioDone();
 *
 *   // To cancel mid-turn (user interrupts):
 *   reset();
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AudioPlaybackQueue } from '../lib/audio-playback.js';

export interface UseAudioPlaybackReturn {
  /** Enqueue one base64-encoded PCM chunk (from an audio_chunk WebSocket frame) */
  enqueueChunk: (base64Pcm: string) => void;
  /** Signal that all chunks for this turn have been received (audio_done frame) */
  onAudioDone: () => void;
  /** True while audio is actively playing */
  isPlaying: boolean;
  /**
   * Resume (or create) the AudioContext.
   *
   * MUST be called from inside an onClick/onTouchStart handler before the first
   * voice turn on iOS Safari. Safe to call on every interaction.
   */
  resumeAudioContext: () => Promise<void>;
  /** Stop playback and clear the queue (e.g., when starting a new turn) */
  reset: () => void;
}

export function useAudioPlayback(): UseAudioPlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const queueRef = useRef<AudioPlaybackQueue | null>(null);

  // Create the queue on first render
  if (!queueRef.current) {
    queueRef.current = new AudioPlaybackQueue({
      sampleRate: 16000,
      onEnded: () => setIsPlaying(false),
    });
  }

  // Cleanup on unmount
  useEffect(() => {
    const q = queueRef.current;
    return () => {
      q?.close().catch(() => { /* ignore cleanup errors */ });
    };
  }, []);

  const resumeAudioContext = useCallback(async () => {
    await queueRef.current?.resumeAudioContext();
  }, []);

  const enqueueChunk = useCallback((base64Pcm: string) => {
    queueRef.current?.enqueueBase64(base64Pcm);
    setIsPlaying(true);
  }, []);

  const onAudioDone = useCallback(() => {
    queueRef.current?.flush();
    // isPlaying will be set to false by the onEnded callback when queue drains
  }, []);

  const reset = useCallback(() => {
    queueRef.current?.reset();
    setIsPlaying(false);
  }, []);

  return {
    enqueueChunk,
    onAudioDone,
    isPlaying,
    resumeAudioContext,
    reset,
  };
}
